import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from "@angular/core";
import { Subscription } from "rxjs";
import { Router } from "@angular/router";
import { SharedService } from "../shared/services/shared.service";
import { AuthService } from "../shared/services/auth.service";
import { BroshService } from "../shared/services/brosh.service";
import { DataService } from "../shared/services/data.service";
import { UsersService } from "../shared/services/users.service";
import { Store } from "@ngrx/store";
import { UI__REQUEST_SET_LANG } from "../shared/ngrx/ngrx-actions";
import { userSelectedClinicId } from "../shared/ngrx/ngrx-selectors";
import { take } from "rxjs/operators";
import { PopupComponent } from "../shared/components/ui/popup.component";
import { TranslateDirective } from "../shared/components/ui/translate.directive";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { SpinnerComponent } from "../shared/components/ui/spinner/spinner.component";
import { FormsModule } from "@angular/forms";
import { LangSwitchComponent } from "../shared/components/lang-switch/lang-switch.component";
import { CheckboxWrapComponent } from "../shared/components/ui/checkbox-wrap.component";
import { LoaderComponent } from "../shared/components/ui/loader/loader.component";

@Component({
  selector: "login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
  imports: [
    PopupComponent,
    TranslateDirective,
    CommonModule,
    TranslateModule,
    SpinnerComponent,
    FormsModule,
    LangSwitchComponent,
    CheckboxWrapComponent,
    LoaderComponent,
  ],
  standalone: true,
})
export class LoginComponent implements OnInit, OnDestroy {
  @ViewChild("loginIputPassword", { static: false })
  loginIputPassword: ElementRef;
  @ViewChild("userNameInput", { static: false }) userNameInput: ElementRef;
  rememberMe: boolean = true;
  typedUserName: string;
  username: string;
  password: string;
  showLogin: boolean = true;
  generalLoader: boolean = false;
  allowShowPassBtn: boolean = true;
  showSpinner: boolean = true;
  generalLoaderSub: Subscription;

  constructor(
    private readonly store: Store,
    private sharedService: SharedService,
    private authService: AuthService,
    private router: Router,
    private usersService: UsersService
  ) {}

  ngOnInit() {
    this.generalLoaderSub = this.sharedService.generalLoader$.subscribe((data) => {
      this.generalLoader = data;
    });

    this.typedUserName = this.sharedService.getCookie("username");
    this.password = this.sharedService.getCookie("password", true);

    if (this.typedUserName && this.password) {
      this.checkExistingSession();
    } else {
      this.showSpinner = false;
    }
  }

  ngOnDestroy() {
    this.generalLoaderSub?.unsubscribe();
  }

  ngAfterViewInit(): void {
    let lang = this.sharedService.getCookie("lang");
    if (lang) {
      this.store.dispatch(
        UI__REQUEST_SET_LANG({
          lang: lang,
        })
      );
    }
  }

  removeInitialLoader() {
    const initialLoadingDiv = document.getElementById("initial_loading");
    initialLoadingDiv &&
      initialLoadingDiv.parentNode.removeChild(initialLoadingDiv);
    this.showSpinner = false;
    this.showLogin = true;
  }

  checkExistingSession() {
    this.showSpinner = true;
    this.showLogin = false;

    this.authService
      .retrieveUserSession(this.typedUserName.toUpperCase())
      .then(
        (data) => {
          this.putRemoveCookies();
          //vaid session
          this.appLogin();
        },
        (reject) => {
          // no session
          this.removeInitialLoader();
        }
      )
      .catch((err) => {
        console.log(err);
        this.removeInitialLoader();
      });
  }

  putRemoveCookies() {
    if (this.rememberMe) {
      this.sharedService.putCookie("typedUserName", this.typedUserName);
      this.sharedService.putCookie("username", this.typedUserName);
      this.sharedService.putCookie("password", this.password, true);
    } else {
      this.sharedService.removeCookie("typedUserName");
      this.sharedService.removeCookie("username");
      this.sharedService.removeCookie("password");
    }
  }

  login(e: any, updateAppUserPassword: boolean = false) {
    if (e) e.stopPropagation();

    this.sharedService.emitGeneralLoaderEvent(true);
    this.username = this.typedUserName.toUpperCase().trim();
    this.putRemoveCookies();

    if (!this.typedUserName || !this.password) {
      this.sharedService.alertWarning("translate.login.inputerror");
      return;
    }

    this.authService
      .loginUser(this.username.toUpperCase(), this.password)
      .then(
        (data) => {
          if (data.value === "EMPTY_TOKEN") {
            this.showLogin = true;
          } else if (data.value === "OK") {
            if (updateAppUserPassword) {
              this.authService.updateUserPassword(this.password).subscribe(
                () => {
                  this.appLogin();
                },
                (err) => {
                  this.handleLoginFailed();
                }
              );
            } else {
              this.appLogin();
            }
          }
        },
        (reject) => {
          this.showLogin = true;
          this.sharedService.alertError(
            reject.error,
            "translate.login.loginFailedMsg"
          );
        }
      )
      .catch((err) => {
        this.showLogin = true;
        console.log(err);
        this.sharedService.alertError(err);
      });
  }

  appLogin() {
    this.store
      .select(userSelectedClinicId)
      .pipe(take(1))
      .subscribe((userSelectedClinicId) => {
        this.authService
          .appSignIn(this.password, userSelectedClinicId)
          .subscribe(
            () => {
              this.getUserDetails();
            },
            (err) => {
              this.handleLoginFailed(err?.message);
            }
          );
      });
  }

  getUserDetails() {
    this.authService.getUserDetails().subscribe(
      (res) => {
        if (res) {
          this.router.navigate([this.sharedService.routeEnum.URL_DASHBOARD]);
        } else {
          this.handleLoginFailed();
        }
      },
      (err) => {
        this.handleLoginFailed();
      }
    );
  }

  handleLoginFailed(customMsg?: string) {
    this.sharedService.alertError(
      customMsg ?? "translate.login.loginFailedMsg"
    );
    this.showSpinner = false;
    this.showLogin = true;
  }

  forgotPassword() {
    this.username = this.typedUserName.toUpperCase();
    if (!this.username) {
      this.sharedService.alertWarning("translate.alert.fillUserName");
      return;
    }

    this.authService
      .forgotPassword(this.username)
      .then(
        (data) => {
          this.sharedService.emitPopupchReceivedEvent({
            subTitle: "translate.alert.passwordChangeSuccessfully",
            icon: "check",
          });

          this.password = this.sharedService.capturedUserPassword;
          this.sharedService.removeCookie("password");
          this.login(null, true);
        },
        (reject) => {
          this.sharedService.alertError(reject.error);
        }
      )
      .catch((err) => {
        console.log(err);
      });
  }

  passwordInputType: "text" | "password";
  showPassword($event: MouseEvent) {
    $event.preventDefault();
    const passwordInput = this.loginIputPassword.nativeElement;
    passwordInput.type =
      passwordInput.type === "password" ? "text" : "password";
    this.passwordInputType = passwordInput.type;
  }
}
